import { Injectable, group } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AttachmentPoint } from '../models/attachmentpoint';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../environments/environment';
import { getFileNameFromResponseContentDisposition, checkIfTruncated, saveFile } from './utils/file-download-helper';

@Injectable()
export class AttachmentPointService {

  AP_API_URL = environment.serverUrl + 'api/1/attachment-points';

  constructor(private httpClient: HttpClient) { }

  getFormattedUrl(pageIndex: number, itemPerPage: number,
                  filters: Map<string, any>, criterias: Map<string, boolean>,
                  stationLink: StationLink) {
    const offset = ((pageIndex - 1) * itemPerPage); // records offset to display the specific range

    let url = this.AP_API_URL + '?pagination=' + offset + '-' + itemPerPage + '&sorts=';

    if (criterias.size === 0) { // if no sorts criterias => sort by tag
      url += 'ap.tag asc';
    } else {
      Array.from(criterias.keys()).forEach(c => url += c + (criterias.get(c) === true ? ' asc' : ' desc') + ',');
      url = url.substring(0, url.length - 1);
    }

    if (filters !== undefined) {

      const filtersAsString = JSON.stringify(filters, (key, value) => {
        if (value !== null && value !== undefined && value !== '') { return value; }
      });

      if (filtersAsString.length > 2) {
        url += '&filters=' + filtersAsString;
      }
    }

    url += '&stationLink=' + StationLink[stationLink];
    return url;
  }

  /**
   *
   * @param pageIndex the current page (start at 1)
   * @param itemPerPage the number of items per page
   * @param filters a hashmap of property => objects
   * @param criterias criterias for sorting : map of properties => order (asc or desc)
   * @param nullsStations boolean indicating if we want ap with no station
   * @param nullsOnly boolean indicating if we ONLY want AP with null stations
   */
  getPaginatedFilteredSortedAttachmentPoints(pageIndex: number, itemPerPage: number,
    filters: Map<string, any>, criterias: Map<string, boolean>,
    stationLink: StationLink): any {

      const url = this.getFormattedUrl(pageIndex, itemPerPage, filters, criterias, stationLink);
    return this.httpClient.get<any>(encodeURI(url), { observe: 'response' }).map(response => {
      return {
        headers: response.headers,
        res: <AttachmentPoint[]>response.body
      };
    }
    );
  }

  downloadXLSFile(pageIndex: number, itemPerPage: number, filters: Map<string, any>, criterias: Map<string, boolean>, stationLink: StationLink): any {
    const url = this.getFormattedUrl(pageIndex, itemPerPage, filters, criterias, stationLink);
    return this.httpClient.get<any>(encodeURI(url), {
      headers: { Accept: 'application/octet-stream' },
      responseType: 'blob' as 'json',
      observe: 'response'
    }).map((response) => {
      const fileName = getFileNameFromResponseContentDisposition(response, 'attachmentpoints.xls');
      const isTruncated = checkIfTruncated(response);
      saveFile(response.body.slice(), fileName);
      return isTruncated;
    });
  }

  delete(ap: AttachmentPoint) {
    return this.httpClient.delete(encodeURI(this.AP_API_URL + '/' + ap.id));
  }

  get(ap: AttachmentPoint): Observable<AttachmentPoint> {
    return this.httpClient.get<AttachmentPoint>(encodeURI(this.AP_API_URL + '/' + ap.id));
  }

  post(ap: AttachmentPoint): Observable<AttachmentPoint> {
    return this.httpClient.post<AttachmentPoint>(this.AP_API_URL, ap);
  }

  put(ap: AttachmentPoint): Observable<AttachmentPoint> {
    return this.httpClient.put<AttachmentPoint>(this.AP_API_URL + '/' + ap.id, ap);
  }

  getAPbyID(apId: any): Observable<AttachmentPoint> {
    return this.httpClient.get<AttachmentPoint>(encodeURI(this.AP_API_URL + '/' + apId));
  }
  /**
   * Return a list of AP attached to a specific station
   * @param stationId
   */
  getAPAttachedtoStation(stationId: any) {
    const url = this.AP_API_URL + '?pagination=0-100&sorts=ap.name asc&stationId=' + stationId + '&stationLink=' + StationLink[StationLink.ONLY_LINKED];
    return this.httpClient.get<any>(encodeURI(url), { observe: 'response' }).map(response => {
      return {
        headers: response.headers,
        res: <AttachmentPoint[]>response.body
      };
    });
  }

  /**
   * Return a list of "free" AP available for a specific group
   * @param groupId
   */
  getFreeAPByGroup(groupId: any): Observable<any> {
    const url = this.AP_API_URL + '/freeaps?pagination=0-100&sorts=ap.name asc&groupId=' + groupId + '&stationLink=' + StationLink[StationLink.ONLY_NULLS];
    return this.httpClient.get<any>(encodeURI(url), { observe: 'response' }).map(response => {
      return {
        headers: response.headers,
        res: <AttachmentPoint[]>response.body
      };
    });
  }

}

export enum StationLink { ALL, ONLY_NULLS, ONLY_LINKED }



// WEBPACK FOOTER //
// ./src/app/services/attachment-point.service.ts